import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Groups';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addMapConnection, fetchTrackedConnections, updateMapSystem } from '../../api/maps';
import { fetchFittings } from '../../api/fittings';
import { ActivityTrail } from '../../components/CorpActivityDock';
import ConsolePanel from '../../components/ConsolePanel';
import MarkdownField from '../../components/MarkdownField';
import StatusBadge from '../../components/StatusBadge';
import SystemGeneralInfoPanel from '../../components/SystemGeneralInfoPanel';
import { C, FONT, hexToRgba, lcarsLabelSx } from '../../theme/tokens';
import { LifeStatus, MapSystem, MassStatus } from '../../types';
import { confirmChirpSx } from './vaultMotion';

const MASS_OPTIONS: MassStatus[] = ['fresh', 'reduced', 'critical', 'unknown'];
const LIFE_OPTIONS: LifeStatus[] = ['stable', 'eol', 'unknown'];

/** Two explicit grids rather than one auto-fit track. auto-fit packs as many
 * columns as fit, which gave the four connection controls three across and
 * one orphaned underneath — a ragged shape that reads as a mistake. Stated
 * counts keep every row of this editor on the same rhythm at every width.
 *
 * The pair grid is also capped, not just floored: a label input stretched to
 * 900px on a wide screen is a worse target than one that stops. */
const GRID_PAIR = { xs: '1fr', sm: 'repeat(2, minmax(0, 340px))' };
const GRID_QUAD = { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' };

/** The editor's own measure. The panel is page-wide; a form filling it is
 * not — notes at 2500px would run well past a readable line. */
const FORM_MAX = 1180;

/**
 * The open entry: what this system is, what the scout wrote about it, and
 * whether that note is shared or classified. Saved on blur rather than per
 * keystroke, to avoid a PATCH per character typed.
 */
const EntryDetail: React.FC<{
  mapId: number;
  system: MapSystem;
  systems: MapSystem[];
  onSaved: (s: MapSystem) => void;
}> = ({ mapId, system, systems, onSaved }) => (
  // Two full-width panels stacked, not a two-up grid: the note is the tall
  // element and wants the width, and a half-width sibling next to it left
  // one column short and the other padded with air. Log Connection is no
  // longer a peer panel at all — see VaultEntryForm's own note.
  <Stack spacing={2} sx={{ pl: { xs: 0, sm: '26px' } }}>
    <ConsolePanel title={system.label || system.name} accent={C.wormhole} corners={false}>
      <VaultEntryForm mapId={mapId} system={system} systems={systems} onSaved={onSaved} />
    </ConsolePanel>
    {/* Same component the system's own Overview tab shows — corp-wide notes
        and linked fittings are keyed on the system, not this vault, so
        reusing it here (rather than a vault-local copy) means an edit made
        from either place is instantly visible from the other. */}
    <SystemGeneralInfoPanel solarSystemId={system.solar_system_id} />
    {/* What's been found and when, for this entry's actual system — the same
        trail CorpActivityDock summons on the system's own Overview tab,
        shown inline here since a vault entry is exactly where "what's the
        history on this hole" gets asked. */}
    <ConsolePanel title="Activity" accent={C.gold} corners={false}>
      <ActivityTrail solarSystemId={system.solar_system_id} systemName={system.label || system.name} />
    </ConsolePanel>
  </Stack>
);

const VaultEntryForm: React.FC<{
  mapId: number;
  system: MapSystem;
  systems: MapSystem[];
  onSaved: (s: MapSystem) => void;
}> = ({ mapId, system, systems, onSaved }) => {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(system.label ?? '');
  const [tag, setTag] = useState(system.tag ?? '');
  const [notes, setNotes] = useState(system.notes ?? '');
  const [fitAnchor, setFitAnchor] = useState<HTMLElement | null>(null);
  const [logging, setLogging] = useState(false);

  // Selecting a different row reuses this component, so the draft fields
  // have to re-seed or the previous system's text follows you across.
  //
  // Keyed on the entry id ALONE, deliberately. Watching the field values too
  // meant every successful save re-ran this — which closed the connection
  // form the moment you saved a note with it open, and fought any keystroke
  // that landed between the PATCH and its response.
  useEffect(() => {
    setLabel(system.label ?? '');
    setTag(system.tag ?? '');
    setNotes(system.notes ?? '');
    setLogging(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [system.id]);

  const mutation = useMutation({
    mutationFn: (
      body: Partial<{ label: string; tag: string; notes: string; isHome: boolean; notesVisibility: 'shared' | 'private' }>,
    ) => updateMapSystem(mapId, system.id, body),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['map-systems', mapId] });
      onSaved(updated);
    },
  });

  // Shares the ['fittings'] cache key with Fittings.tsx and
  // SystemGeneralInfoPanel — whichever of the three loads first pays for the
  // fetch, the other two read it for free.
  const { data: ownFittings } = useQuery({ queryKey: ['fittings'], queryFn: fetchFittings });

  /** Appends a real `/fitting/<id>` reference — the same token the note
   * renderer already linkifies — rather than asking the scout to remember or
   * type an id. Saved immediately rather than waiting for a blur that a menu
   * click doesn't reliably produce. */
  const insertFittingLink = (fittingId: number, name: string) => {
    const insertion = `- [${name}](/fitting/${fittingId})`;
    const next = notes.trim().length > 0 ? `${notes}\n${insertion}` : insertion;
    setNotes(next);
    setFitAnchor(null);
    mutation.mutate({ notes: next });
  };

  const visibility = system.notes_visibility ?? 'shared';

  return (
    <Stack spacing={2} sx={{ maxWidth: FORM_MAX }}>
      {/* Identity row: what this entry is called and where it's filed. Both
          are short single-line values, so they share one row on any screen
          wide enough rather than each claiming a full line. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_PAIR, justifyContent: 'start', gap: 1.5 }}>
        <Field label="Label" hint={`Optional nickname. Blank shows ${system.name}.`}>
          <TextField
            fullWidth
            size="small"
            placeholder={system.name}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => label !== (system.label ?? '') && mutation.mutate({ label })}
          />
        </Field>
        <Field label="Group" hint="Which drawer of this vault it's filed in.">
          {/* The examples deliberately avoid the word "home": this vault also
              has a single home *system*, and a group happening to be called
              "home" made two unrelated controls read as the same setting. */}
          <TextField
            fullWidth
            size="small"
            placeholder="staging, farm, rolling&hellip;"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            onBlur={() => tag !== (system.tag ?? '') && mutation.mutate({ tag })}
          />
        </Field>
      </Box>

      <Field
        label="Notes"
        hint="Markdown. J123456 and /fitting/12 link themselves."
        action={
          ownFittings && ownFittings.length > 0 ? (
            <>
              <Button
                size="small"
                startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => setFitAnchor(e.currentTarget)}
                sx={{ color: C.cyan, fontSize: '0.68rem', px: 0.75, minWidth: 0 }}
              >
                Fitting
              </Button>
              <Menu anchorEl={fitAnchor} open={!!fitAnchor} onClose={() => setFitAnchor(null)}>
                {ownFittings.map((f) => (
                  <MenuItem key={f.id} onClick={() => insertFittingLink(f.id, f.name)}>
                    {f.name} &mdash; {f.shipName}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : null
        }
      >
        <MarkdownField
          value={notes}
          onChange={setNotes}
          onCommit={() => notes !== (system.notes ?? '') && mutation.mutate({ notes })}
          placeholder={'POS at planet 4.\n\n- C5 static rolls fast\n- **Do not** log off here'}
        />
      </Field>

      {/* Designations: the two per-entry flags, side by side because they are
          the same kind of decision — who sees this, and what role it plays in
          the vault. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_PAIR, justifyContent: 'start', gap: 1.5 }}>
        <Field label="Who can read the note">
          <ToggleButtonGroup
            size="small"
            exclusive
            fullWidth
            value={visibility}
            onChange={(_, v) => v && v !== visibility && mutation.mutate({ notesVisibility: v })}
            sx={segmentedSx}
          >
            <ToggleButton value="shared">
              <PublicIcon sx={{ fontSize: 14, mr: 0.5 }} /> Everyone
            </ToggleButton>
            <ToggleButton value="private">
              <LockIcon sx={{ fontSize: 14, mr: 0.5 }} /> Only me
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography sx={hintSx}>
            {visibility === 'private'
              ? 'The entry still lists; the note text is withheld.'
              : 'Everyone with vault access can read it.'}
          </Typography>
        </Field>

        {/* Was a bare switch labelled "Home system of this vault", one line
            under a field whose example was "home" — two different things
            wearing the same word. It is now stated as what it does: one
            entry per vault holds this, and it anchors the chain. */}
        <Field label="Role in this vault">
          <ToggleButtonGroup
            size="small"
            exclusive
            fullWidth
            value={system.is_home_system ? 'home' : 'normal'}
            onChange={(_, v) => v && mutation.mutate({ isHome: v === 'home' })}
            sx={segmentedSx}
          >
            <ToggleButton value="normal">Saved system</ToggleButton>
            <ToggleButton value="home">
              <HomeIcon sx={{ fontSize: 14, mr: 0.5 }} /> Home
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography sx={hintSx}>
            {system.is_home_system
              ? 'Anchors the diagram and leads the list.'
              : 'Only one entry per vault can be its home.'}
          </Typography>
        </Field>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        {/* Logging a connection is an occasional act, not part of editing an
            entry — as a permanently-open peer panel it took half the editor's
            width to sit empty. Behind its own button it costs one line until
            it's wanted, and opens with the jump the tracker already saw. */}
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<AddLinkIcon sx={{ fontSize: 16 }} />}
          onClick={() => setLogging((v) => !v)}
        >
          {logging ? 'Close' : 'Log a connection'}
        </Button>
        {mutation.isSuccess && (
          <Box sx={confirmChirpSx}>
            <StatusBadge label="Saved" accent={C.cyan} />
          </Box>
        )}
        {mutation.isError && (
          <Typography sx={{ color: C.red, fontSize: '0.72rem' }}>Couldn&rsquo;t save — try again.</Typography>
        )}
      </Stack>

      <Collapse in={logging} timeout={200} unmountOnExit>
        <ConnectionForm mapId={mapId} fromSystem={system} systems={systems} onLogged={() => setLogging(false)} />
      </Collapse>
    </Stack>
  );
};

/** One labelled control. Every field in the editor goes through this, which
 * is what makes the label scale, the gap under it and the hint beneath read
 * identically in all six places rather than being re-tuned per field. */
const Field: React.FC<{ label: string; hint?: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  label,
  hint,
  action,
  children,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.6, minHeight: 22 }}>
      <Typography sx={lcarsLabelSx(C.textDim, '0.64rem')}>{label}</Typography>
      <Box sx={{ flex: 1 }} />
      {action}
    </Stack>
    {children}
    {hint && <Typography sx={hintSx}>{hint}</Typography>}
  </Box>
);

const hintSx = {
  fontFamily: FONT.body,
  fontSize: '0.7rem',
  color: C.textDim,
  mt: 0.5,
  lineHeight: 1.4,
} as const;

/** The one segmented-control treatment, shared by both toggle pairs so they
 * read as the same kind of choice. */
const segmentedSx = {
  '& .MuiToggleButton-root': {
    fontFamily: FONT.sub,
    fontSize: '0.72rem',
    letterSpacing: '0.06em',
    textTransform: 'none',
    color: C.textDim,
    borderColor: C.hairline,
    py: 0.6,
  },
  '& .Mui-selected': {
    color: `${C.cyan} !important`,
    borderColor: `${hexToRgba(C.cyan, 0.5)} !important`,
    backgroundColor: `${hexToRgba(C.cyan, 0.08)} !important`,
  },
} as const;

const ConnectionForm: React.FC<{
  mapId: number;
  fromSystem: MapSystem;
  systems: MapSystem[];
  onLogged: () => void;
}> = ({ mapId, fromSystem, systems, onLogged }) => {
  const queryClient = useQueryClient();
  const [toId, setToId] = useState<number | ''>('');
  const [sigCode, setSigCode] = useState('');
  const [massStatus, setMassStatus] = useState<MassStatus>('unknown');
  const [lifeStatus, setLifeStatus] = useState<LifeStatus>('unknown');

  // The tracker already watched somebody fly this hole. Reading that back as
  // the default destination is the difference between confirming a jump the
  // app observed and re-entering it from memory.
  const { data: tracked } = useQuery({
    queryKey: ['map-tracked', mapId],
    queryFn: () => fetchTrackedConnections(mapId),
  });

  const suggestion = useMemo(() => {
    if (!tracked) return null;
    for (const t of tracked) {
      // Either direction: the poller records the jump as it happened, and a
      // scout logging it afterwards may be sitting at either end.
      const otherId =
        t.from_system_id === fromSystem.solar_system_id
          ? t.to_system_id
          : t.to_system_id === fromSystem.solar_system_id
            ? t.from_system_id
            : null;
      if (otherId === null) continue;
      const match = systems.find((s) => s.solar_system_id === otherId);
      if (match) return match;
    }
    return null;
  }, [tracked, systems, fromSystem.solar_system_id]);

  // Preselect once the suggestion resolves, but never overwrite a choice the
  // scout has already made.
  useEffect(() => {
    if (suggestion && toId === '') setToId(suggestion.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion?.id]);

  const mutation = useMutation({
    mutationFn: () =>
      addMapConnection(mapId, {
        fromMapSystemId: fromSystem.id,
        toMapSystemId: toId || undefined,
        signatureCode: sigCode || undefined,
        massStatus,
        lifeStatus,
      }),
    onSuccess: () => {
      setSigCode('');
      queryClient.invalidateQueries({ queryKey: ['map-connections', mapId] });
      onLogged();
    },
  });

  const destinations = systems.filter((s) => s.id !== fromSystem.id);

  return (
    <Box sx={{ border: `1px solid ${C.hairline}`, borderRadius: '8px', p: 1.75, maxWidth: FORM_MAX }}>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography sx={lcarsLabelSx(C.cyan, '0.64rem')}>New connection</Typography>
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.textDim }}>
          from {fromSystem.label || fromSystem.name}
        </Typography>
        {suggestion && toId === suggestion.id && (
          <Tooltip title="A linked character was seen making this jump" disableInteractive>
            <Box>
              <StatusBadge label="observed jump" accent={C.gold} dim />
            </Box>
          </Tooltip>
        )}
      </Stack>

      {/* Four across on a wide screen, two on a tablet, stacked on a phone —
          never three-and-an-orphan. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_QUAD, gap: 1.5 }}>
        <Field label="Leads to">
          <TextField fullWidth size="small" select value={toId} onChange={(e) => setToId(Number(e.target.value) || '')}>
            <MenuItem value="">Unknown</MenuItem>
            {destinations.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.label || s.name}
                {suggestion?.id === s.id ? ' · observed' : ''}
              </MenuItem>
            ))}
          </TextField>
        </Field>
        <Field label="Signature">
          <TextField
            fullWidth
            size="small"
            placeholder="ABC-123"
            value={sigCode}
            onChange={(e) => setSigCode(e.target.value.toUpperCase())}
          />
        </Field>
        <Field label="Mass">
          <TextField
            fullWidth
            size="small"
            select
            value={massStatus}
            onChange={(e) => setMassStatus(e.target.value as MassStatus)}
          >
            {MASS_OPTIONS.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
        </Field>
        <Field label="Lifetime">
          <TextField
            fullWidth
            size="small"
            select
            value={lifeStatus}
            onChange={(e) => setLifeStatus(e.target.value as LifeStatus)}
          >
            {LIFE_OPTIONS.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </TextField>
        </Field>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1.75 }}>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Logging…' : 'Log connection'}
        </Button>
        {mutation.isError && (
          <Typography sx={{ color: C.red, fontSize: '0.72rem' }}>Couldn&rsquo;t log that — try again.</Typography>
        )}
      </Stack>
    </Box>
  );
};

export default EntryDetail;
